import type { RuleBoxChoice } from "@loot-os/api-contract";
import { addChoice, rankChoiceItems } from "@loot-os/api-contract/shopifyRuleBoxForm";
import { useState } from "react";
import { fieldValue } from "./fieldValue";

/** How many matches the dropdown lists before it scrolls. */
const DROPDOWN_ROWS = 8;
const ROW_HEIGHT = 34;

const DROPDOWN = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 20,
  marginTop: "4px",
  maxHeight: `${DROPDOWN_ROWS * ROW_HEIGHT}px`,
  overflowY: "auto",
  background: "#fff",
  border: "1px solid #e3e3e3",
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
  padding: "4px 0",
} as const;

const OPTION = {
  display: "block",
  width: "100%",
  height: `${ROW_HEIGHT}px`,
  padding: "0 12px",
  border: "none",
  background: "transparent",
  textAlign: "left",
  font: "inherit",
  cursor: "pointer",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const;

const BROWSE = {
  maxHeight: "240px",
  overflowY: "auto",
  border: "1px solid #e3e3e3",
  borderRadius: "8px",
  padding: "8px",
} as const;

/** `value` with the part matching `query` in bold, so the dropdown shows why each row matched. */
function Highlighted({ value, query }: { value: string; query: string }) {
  const at = query ? value.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (at < 0) return <>{value}</>;
  return (
    <>
      {value.slice(0, at)}
      <strong>{value.slice(at, at + query.length)}</strong>
      {value.slice(at + query.length)}
    </>
  );
}

/**
 * A multi-select over the shop's own choices (tags, vendors, product types, collections, option
 * values, category values): the chosen ones as removable chips, a field that autocompletes from
 * every choice the shop has (arrow keys and Enter, or a click), and "Browse all" to see and pick
 * from the whole list. A choice is stored by `id` and shown by `label` (the same text for a tag).
 * With `freeText`, a value the shop does not use yet can be added too (a tag the merchant is about
 * to give products); without it, only one of `options`.
 */
export function ChoicePicker({
  label,
  details,
  placeholder,
  options,
  chosen,
  freeText,
  noun,
  missingLabel,
  onChange,
}: {
  label: string;
  details: string;
  placeholder: string;
  options: RuleBoxChoice[];
  chosen: string[];
  freeText: boolean;
  /** What one option is called, plural, for "Browse all 42 tags". */
  noun: string;
  /** The chip of a chosen id the shop no longer has ("Deleted collection"); else the id itself. */
  missingLabel?: string;
  onChange: (next: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [browsing, setBrowsing] = useState(false);
  const typed = query.trim();
  const matches = rankChoiceItems(options, chosen, typed);
  const known = options.find((o) => o.label.toLowerCase() === typed.toLowerCase());
  const addable = freeText ? typed : (known?.id ?? "");
  const open = focused && matches.length > 0;
  const active = Math.min(highlight, matches.length - 1);
  const labelOf = (id: string) =>
    options.find((o) => o.id.toLowerCase() === id.toLowerCase())?.label ?? missingLabel ?? id;

  function add(id: string) {
    onChange(addChoice(chosen, id));
    setQuery("");
    setHighlight(0);
  }

  function remove(id: string) {
    onChange(chosen.filter((v) => v !== id));
  }

  function toggle(id: string) {
    const lower = id.toLowerCase();
    if (chosen.some((v) => v.toLowerCase() === lower)) {
      onChange(chosen.filter((v) => v.toLowerCase() !== lower));
    } else add(id);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (!matches.length) return;
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      setHighlight((active + step + matches.length) % matches.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      // A highlighted match wins once the merchant has typed or moved to it; else the typed text.
      const picked = open && (typed || highlight > 0) ? matches[active]?.id : undefined;
      const value = picked ?? addable;
      if (value) add(value);
    } else if (e.key === "Escape") {
      setFocused(false);
    }
  }

  const sortedAll = [...options].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
  );

  return (
    <s-stack gap="small-200">
      <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="end">
        <div style={{ position: "relative" }}>
          <s-text-field
            label={label}
            placeholder={placeholder}
            value={query}
            autocomplete="off"
            onInput={(e) => {
              setQuery(fieldValue(e));
              setHighlight(0);
              setFocused(true);
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={onKeyDown}
          />
          {open && (
            <div role="listbox" aria-label={label} style={DROPDOWN}>
              {matches.map((choice, i) => (
                <button
                  key={choice.id}
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  tabIndex={-1}
                  style={{ ...OPTION, background: i === active ? "#f1f1f1" : "transparent" }}
                  // Before the field's blur closes the list.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(choice.id);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                >
                  <Highlighted value={choice.label} query={typed} />
                </button>
              ))}
            </div>
          )}
        </div>
        <s-button disabled={!addable || undefined} onClick={() => add(addable)}>
          Add
        </s-button>
      </s-grid>
      {/* Outside the field, so the Add button lines up with the input rather than this hint. */}
      <s-text color="subdued">{details}</s-text>
      {chosen.length > 0 && (
        <s-stack direction="inline" gap="small-200">
          {chosen.map((id) => (
            <s-clickable-chip
              key={id}
              color="strong"
              removable
              accessibilityLabel={`Remove ${labelOf(id)}`}
              onClick={() => remove(id)}
              onRemove={() => remove(id)}
            >
              {labelOf(id)}
            </s-clickable-chip>
          ))}
        </s-stack>
      )}
      {typed && !freeText && !known && matches.length === 0 && (
        <s-text color="subdued">None of your products use "{typed}".</s-text>
      )}
      {options.length > 0 && (
        <s-stack gap="small-200">
          <s-stack direction="inline">
            <s-button variant="tertiary" onClick={() => setBrowsing(!browsing)}>
              {browsing ? `Hide ${noun}` : `Browse all ${options.length} ${noun}`}
            </s-button>
          </s-stack>
          {browsing && (
            <div style={BROWSE}>
              <s-stack direction="inline" gap="small-200">
                {sortedAll.map((choice) => {
                  const picked = chosen.some((v) => v.toLowerCase() === choice.id.toLowerCase());
                  return (
                    <s-clickable-chip
                      key={choice.id}
                      color={picked ? "strong" : undefined}
                      icon={picked ? undefined : "plus"}
                      accessibilityLabel={picked ? `Remove ${choice.label}` : `Add ${choice.label}`}
                      onClick={() => toggle(choice.id)}
                    >
                      {choice.label}
                    </s-clickable-chip>
                  );
                })}
              </s-stack>
            </div>
          )}
        </s-stack>
      )}
    </s-stack>
  );
}
